import React, { useEffect, useState, useCallback } from "react";
import DataTable from "react-data-table-component";
import {
  Card,
  Row,
  Col,
  Input,
  Button,
  Space,
  Typography,
  Tag,
  Empty,
  Badge,
  Tooltip,
  Select,
  Modal,
  Alert,
  Skeleton,
  Avatar,
  Dropdown,
  Menu,
  Statistic,
  Divider,
  Grid,
  DatePicker
} from "antd";
import {
  SearchOutlined,
  FilterOutlined,
  ReloadOutlined,
  PlusOutlined,
  EyeOutlined,
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  IdcardOutlined,
  WalletOutlined,
  MoreOutlined,
  DownloadOutlined,
  SettingOutlined,
  TeamOutlined,
  LineChartOutlined,
  UserSwitchOutlined,
  DollarOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined
} from "@ant-design/icons";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { getRequest } from "../../../Utils/AppApi";

const { Title, Text } = Typography;
const { Option } = Select;
const { useBreakpoint } = Grid;

dayjs.extend(relativeTime);

const UsersListPage = ({ history }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedUser, setSelectedUser] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });
  const [selectedDate, setSelectedDate] = useState(dayjs()); // Default to current date

  const screens = useBreakpoint();
  const isMobile = !screens.md;

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    kycVerified: 0,
    todayRegistered: 0,
    totalWalletBalance: 0,
    averageWalletBalance: 0
  });

  // Fetch Users API Call
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      
      params.append('page', pagination.page.toString());
      params.append('limit', pagination.limit.toString());
      
      if (searchQuery && searchQuery.trim() !== "") {
        params.append('search', searchQuery);
      }

      if (statusFilter && statusFilter !== "all") {
        params.append('status', statusFilter);
      }

      // Add date parameter
      params.append('date', selectedDate.format('YYYY-MM-DD'));

      const response = await getRequest(`/admin/getAllUsers?${params.toString()}`, null, history);
      
      if (response?.success) {
        setUsers(response.data || []);
        
        // Calculate stats
        const userData = response.data || [];
        const today = dayjs().format('YYYY-MM-DD');
        
        // Calculate wallet statistics
        const totalWalletBalance = userData.reduce((sum, user) => {
          const balance = user.walletData?.closingBalance || user.walletData?.currentBalance || 0;
          return sum + balance;
        }, 0);
        
        const averageWalletBalance = userData.length > 0 ? totalWalletBalance / userData.length : 0;
        
        setStats({
          total: userData.length,
          active: userData.filter(u => u.status === true).length,
          kycVerified: userData.filter(u => u.KYCStatus === 3).length,
          todayRegistered: userData.filter(u => 
            dayjs(u.createdAt).format('YYYY-MM-DD') === today
          ).length,
          totalWalletBalance,
          averageWalletBalance
        });
        
        if (response.pagination) {
          setPagination(prev => ({
            ...prev,
            total: response.pagination.total || 0,
            pages: response.pagination.pages || 1,
            page: response.pagination.page || 1,
            limit: response.pagination.limit || 10
          }));
        } else {
          setPagination(prev => ({
            ...prev,
            total: response.data?.length || 0,
            pages: Math.ceil((response.data?.length || 0) / pagination.limit)
          }));
        }
      } else {
        setError(response?.message || "Failed to load users");
        toast.error(response?.message || "Failed to load users");
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      setError("Error loading users");
      toast.error("Error loading users");
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, searchQuery, statusFilter, selectedDate, history]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleSearch = () => {
    if (searchText.trim() === "") {
      toast.warning("Please enter search text");
      return;
    }
    setSearchQuery(searchText);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleClearSearch = () => {
    setSearchText("");
    setSearchQuery("");
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleViewLedger = (user) => {
    history.push(`/user-ledger-details/${user._id || user.id}`, {
      user
    });
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return dayjs(dateString).format("DD MMM YYYY, hh:mm A");
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const getStatusTag = (user) => {
    if (user.status === true || user.status === "active") {
      return (
        <Tag 
          color="green" 
          icon={<CheckCircleOutlined />}
          style={{ borderRadius: '12px', padding: '2px 8px' }}
        >
          Active
        </Tag>
      );
    } else {
      return (
        <Tag 
          color="red" 
          icon={<CloseCircleOutlined />}
          style={{ borderRadius: '12px', padding: '2px 8px' }}
        >
          Inactive
        </Tag>
      );
    }
  };

  const getKycTag = (kycStatus) => {
    const config = {
      0: { color: 'default', text: 'Not Started', icon: <IdcardOutlined /> },
      1: { color: 'blue', text: 'Submitted', icon: <IdcardOutlined /> },
      2: { color: 'orange', text: 'Pending', icon: <IdcardOutlined /> },
      3: { color: 'green', text: 'Verified', icon: <CheckCircleOutlined /> }
    };
    
    const { color, text, icon } = config[kycStatus] || config[0];
    
    return (
      <Tag 
        color={color} 
        icon={icon}
        style={{ borderRadius: '12px', padding: '2px 8px' }}
      >
        {text}
      </Tag>
    );
  };

  const handleResetFilters = () => {
    setSearchText("");
    setSearchQuery("");
    setStatusFilter("all");
    setSelectedDate(dayjs()); // Reset to current date
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Render wallet balance information
  const renderWalletInfo = (user) => {
    if (!user.walletData) return "N/A";
    
    const { openingBalance, closingBalance, transactionsCount, currentBalance } = user.walletData;
    
    if (openingBalance !== undefined && closingBalance !== undefined) {
      // For selected date view
      const netChange = closingBalance - openingBalance;
      const isPositive = netChange >= 0;
      
      return (
        <Space direction="vertical" size={2} style={{ width: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Opening:</Text>
            <Text strong style={{ fontSize: 13, color: '#1890ff' }}>
              {formatCurrency(openingBalance)}
            </Text>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Closing:</Text>
            <Text strong style={{ fontSize: 13, color: '#52c41a' }}>
              {formatCurrency(closingBalance)}
            </Text>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
            <Text type="secondary" style={{ fontSize: 11 }}>Net:</Text>
            <Tag 
              color={isPositive ? 'green' : 'red'} 
              size="small"
              style={{ 
                borderRadius: '10px',
                padding: '0 6px',
                fontSize: 11,
                margin: 0
              }}
            >
              {isPositive ? '+' : ''}{formatCurrency(netChange)}
            </Tag>
          </div>
          {transactionsCount > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 2 }}>
              <Text type="secondary" style={{ fontSize: 10 }}>Transactions:</Text>
              <Badge 
                count={transactionsCount} 
                size="small" 
                style={{ 
                  backgroundColor: '#8c8c8c',
                  fontSize: 10
                }} 
              />
            </div>
          )}
        </Space>
      );
    } else {
      // For current balance view
      return (
        <Space direction="vertical" size={2} style={{ width: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Current:</Text>
            <Text strong style={{ fontSize: 14, color: '#3f8600' }}>
              {formatCurrency(currentBalance || 0)}
            </Text>
          </div>
          {user.walletData.lastTransactionDate && (
            <Text type="secondary" style={{ fontSize: 10 }}>
              Updated: {dayjs(user.walletData.lastTransactionDate).format('DD MMM')}
            </Text>
          )}
        </Space>
      );
    }
  };

  // Action Menu for each user
  const getActionMenu = (user) => (
    <Menu
      items={[
        {
          key: 'view-ledger',
          label: 'View Ledger',
          icon: <WalletOutlined />,
          onClick: () => handleViewLedger(user)
        },
        {
          key: 'send-email',
          label: 'Send Email',
          icon: <MailOutlined />,
          onClick: () => toast.info(`Email sent to ${user.email}`)
        },
        {
          key: 'send-sms',
          label: 'Send SMS',
          icon: <PhoneOutlined />,
          onClick: () => toast.info(`SMS sent to ${user.mobile}`)
        }
      ]}
    />
  );

  // Table Columns
  const columns = [
    {
      name: "USER",
      selector: row => row.username,
      sortable: true,
      minWidth: "250px",
      cell: row => (
        <Space align="center">
          <Avatar
            size={48}
            style={{
              background: 'linear-gradient(135deg, #667eea, #764ba2)',
              fontWeight: 600,
              fontSize: 18
            }}
          >
            {row.name?.charAt(0)?.toUpperCase() || row.username?.charAt(0)?.toUpperCase() || "U"}
          </Avatar>
          <div style={{ lineHeight: 1.2 }}>
            <Text strong style={{ display: "block", fontSize: 15 }}>
              {row.name || "N/A"}
            </Text>
            <Text type="secondary" style={{ fontSize: 13 }}>
              @{row.username}
            </Text>
            <div style={{ marginTop: 4 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                <CalendarOutlined style={{ marginRight: 4 }} />
                {dayjs(row.createdAt).format("DD MMM YYYY")}
              </Text>
            </div>
          </div>
        </Space>
      )
    },
    {
      name: "CONTACT",
      selector: row => row.mobile,
      sortable: true,
      minWidth: "180px",
      cell: row => (
        <div>
          <Space direction="vertical" size={2}>
            <Text style={{ display: "flex", alignItems: "center" }}>
              <PhoneOutlined style={{ marginRight: 8, color: '#1890ff' }} />
              {row.mobile || "N/A"}
            </Text>
            {row.email && (
              <Text type="secondary" style={{ fontSize: 12, display: "flex", alignItems: "center" }}>
                <MailOutlined style={{ marginRight: 8 }} />
                {row.email}
              </Text>
            )}
            {row.pancard && (
              <Tag color="default" style={{ fontSize: 11, marginTop: 4 }}>
                PAN: {row.pancard}
              </Tag>
            )}
          </Space>
        </div>
      )
    },
    {
      name: "WALLET BALANCE",
      key: "wallet",
      minWidth: "200px",
      cell: row => renderWalletInfo(row)
    },
    {
      name: "ACTIONS",
      right: true,
      width: "80px",
      cell: row => (
        <Dropdown overlay={getActionMenu(row)} trigger={['click']}>
          <Button
            type="text"
            icon={<MoreOutlined />}
            size="small"
            style={{ color: '#8c8c8c' }}
          />
        </Dropdown>
      )
    }
  ];

  // Custom pagination component
  const CustomPagination = () => (
    <div style={{ 
      padding: '20px 0', 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center',
      borderTop: '1px solid #f0f0f0'
    }}>
      <Text type="secondary">
        Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} users
      </Text>
      <Space>
        <Button
          size="middle"
          disabled={pagination.page === 1}
          onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
          style={{ borderRadius: '6px' }}
        >
          Previous
        </Button>
        <div style={{ 
          background: '#f0f2f5', 
          padding: '6px 12px', 
          borderRadius: '6px',
          minWidth: '80px',
          textAlign: 'center'
        }}>
          <Text strong>{pagination.page} of {pagination.pages}</Text>
        </div>
        <Button
          size="middle"
          disabled={pagination.page >= pagination.pages}
          onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
          style={{ borderRadius: '6px' }}
        >
          Next
        </Button>
      </Space>
    </div>
  );

  return (
    <div style={{ 
      padding: isMobile ? 16 : 24,
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      minHeight: '100vh'
    }}>
      {/* Header Section */}
      <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
        <Col span={24}>
          <div style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: '16px',
            padding: '24px',
            color: 'white'
          }}>
            <Row align="middle" gutter={[24, 24]}>
              <Col xs={24} md={12}>
                <Space direction="vertical" size={8}>
                  <Title level={2} style={{ color: 'white', margin: 0 }}>
                    <TeamOutlined /> Users Management
                  </Title>
                  <Text style={{ color: 'rgba(255,255,255,0.8)' }}>
                    Manage and monitor all registered users in the platform
                  </Text>
                  <div style={{ marginTop: 8 }}>
                    <Tag color="default" style={{ background: 'rgba(255,255,255,0.2)', color: 'white' }}>
                      <CalendarOutlined /> Viewing: {selectedDate.format('DD MMM YYYY')}
                    </Tag>
                  </div>
                </Space>
              </Col>
              <Col xs={24} md={12}>
                <Row gutter={[16, 16]}>
                  <Col xs={12} sm={6}>
                    <Statistic
                      title="Total Users"
                      value={stats.total}
                      prefix={<UserOutlined />}
                      valueStyle={{ color: 'white' }}
                    />
                  </Col>
                  <Col xs={12} sm={6}>
                    <Statistic
                      title="Active"
                      value={stats.active}
                      prefix={<CheckCircleOutlined />}
                      valueStyle={{ color: '#52c41a' }}
                    />
                  </Col>
                  <Col xs={12} sm={6}>
                    <Statistic
                      title="Total Wallet"
                      value={formatCurrency(stats.totalWalletBalance)}
                      prefix={<DollarOutlined />}
                      valueStyle={{ color: 'white' }}
                    />
                  </Col>
                  <Col xs={12} sm={6}>
                    <Statistic
                      title="Avg Wallet"
                      value={formatCurrency(stats.averageWalletBalance)}
                      prefix={<WalletOutlined />}
                      valueStyle={{ color: '#faad14' }}
                    />
                  </Col>
                </Row>
              </Col>
            </Row>
          </div>
        </Col>
      </Row>

      {/* Main Content */}
      <Card
        style={{
          borderRadius: '16px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          border: 'none',
          overflow: 'hidden'
        }}
        bodyStyle={{ padding: isMobile ? 16 : 24 }}
      >
        {/* Error Alert */}
        {error && (
          <Alert
            message="Error"
            description={error}
            type="error"
            showIcon
            style={{ 
              marginBottom: 24,
              borderRadius: '8px'
            }}
            closable
            onClose={() => setError(null)}
          />
        )}

        {/* Search and Filter Section */}
        <div style={{
          background: '#f8f9fa',
          padding: '20px',
          borderRadius: '12px',
          marginBottom: 24
        }}>
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} md={8} lg={6}>
              <Input
                placeholder="Search users (exact match)..."
                prefix={<SearchOutlined style={{ color: '#8c8c8c' }} />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyPress={handleKeyPress}
                allowClear
                onClear={handleClearSearch}
                size="large"
                style={{
                  borderRadius: '8px',
                  border: '1px solid #e8e8e8'
                }}
              />
            </Col>
            <Col xs={24} md={8} lg={6}>
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={handleSearch}
                loading={loading}
                block
                size="large"
                style={{
                  borderRadius: '8px',
                  background: 'linear-gradient(135deg, #667eea, #764ba2)',
                  border: 'none',
                  height: '40px'
                }}
              >
                Search
              </Button>
            </Col>
            <Col xs={24} md={8} lg={6}>
              <DatePicker
                value={selectedDate}
                onChange={(date) => {
                  setSelectedDate(date || dayjs());
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                format="DD MMM YYYY"
                style={{ width: '100%' }}
                size="large"
                allowClear={false}
                suffixIcon={<CalendarOutlined />}
              />
            </Col>
            <Col xs={24} md={8} lg={6}>
              <Space style={{ width: '100%', justifyContent: isMobile ? 'flex-start' : 'flex-end' }}>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={fetchUsers}
                  loading={loading}
                  size="large"
                  style={{ borderRadius: '8px' }}
                >
                  Refresh
                </Button>
                <Button
                  icon={<DownloadOutlined />}
                  size="large"
                  style={{ borderRadius: '8px' }}
                  onClick={() => toast.info("Export feature coming soon")}
                >
                  Export
                </Button>
              </Space>
            </Col>
          </Row>

          {/* Search Info */}
          {searchQuery && (
            <div style={{ marginTop: 16, padding: '12px', background: 'white', borderRadius: '8px' }}>
              <Space align="center">
                <Text strong>Search Results: </Text>
                <Tag color="blue" style={{ fontSize: '14px', padding: '4px 12px' }}>
                  {searchQuery}
                </Tag>
                <Text type="secondary">(Exact match only)</Text>
                <Button 
                  type="link" 
                  onClick={handleClearSearch}
                  style={{ marginLeft: 'auto' }}
                >
                  Clear Search
                </Button>
              </Space>
            </div>
          )}
        </div>

        {/* Date Info */}
        <div style={{ 
          marginBottom: 24,
          padding: '16px',
          background: 'linear-gradient(135deg, #f0f7ff 0%, #e6f7ff 100%)',
          borderRadius: '8px',
          border: '1px solid #91d5ff'
        }}>
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} md={16}>
              <Space direction="vertical" size={2}>
                <Text strong>
                  <CalendarOutlined /> Viewing Wallet Balances for: {selectedDate.format('DD MMMM YYYY')}
                </Text>
                <Text type="secondary" style={{ fontSize: 13 }}>
                  Showing opening and closing balances for each user on the selected date
                </Text>
              </Space>
            </Col>
            <Col xs={24} md={8}>
              <div style={{ display: 'flex', justifyContent: isMobile ? 'flex-start' : 'flex-end', gap: 8 }}>
                <Button
                  size="middle"
                  onClick={() => setSelectedDate(dayjs())}
                  type={selectedDate.isSame(dayjs(), 'day') ? 'primary' : 'default'}
                  style={{ borderRadius: '6px' }}
                >
                  Today
                </Button>
                <Button
                  size="middle"
                  onClick={() => setSelectedDate(selectedDate.subtract(1, 'day'))}
                  style={{ borderRadius: '6px' }}
                >
                  Previous Day
                </Button>
                <Button
                  size="middle"
                  onClick={() => setSelectedDate(selectedDate.add(1, 'day'))}
                  style={{ borderRadius: '6px' }}
                >
                  Next Day
                </Button>
              </div>
            </Col>
          </Row>
        </div>

        {/* Action Buttons */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: 24,
          paddingBottom: 16,
          borderBottom: '1px solid #f0f0f0'
        }}>
          <div>
            <Title level={5} style={{ margin: 0 }}>
              <UserSwitchOutlined /> User List
            </Title>
            <Text type="secondary">
              Total: {pagination.total} users found • Date: {selectedDate.format('DD MMM YYYY')}
            </Text>
          </div>
          <div>
            <Space>
              <Button
                type="primary"
                icon={<WalletOutlined />}
                size="large"
                style={{
                  background: 'linear-gradient(135deg, #52c41a, #73d13d)',
                  border: 'none',
                  borderRadius: '8px'
                }}
                onClick={() => toast.info("Wallet summary coming soon")}
              >
                Wallet Summary
              </Button>
            </Space>
          </div>
        </div>

        {/* Data Table */}
        {loading && users.length === 0 ? (
          <Skeleton active paragraph={{ rows: 10 }} />
        ) : (
          <>
            <DataTable
              columns={columns}
              data={users}
              progressPending={loading}
              pagination={false}
              highlightOnHover
              customStyles={{
                headCells: {
                  style: {
                    backgroundColor: '#f8f9fa',
                    fontWeight: 600,
                    fontSize: '14px',
                    color: '#595959',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    borderBottom: '2px solid #e8e8e8'
                  },
                },
                rows: {
                  style: {
                    backgroundColor: 'white',
                    '&:hover': {
                      backgroundColor: '#fafafa',
                    },
                  },
                  highlightOnHoverStyle: {
                    backgroundColor: '#f0f7ff',
                  },
                },
                cells: {
                  style: {
                    padding: '16px 8px',
                  },
                },
              }}
              noDataComponent={
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    <div style={{ textAlign: 'center' }}>
                      <Title level={4} style={{ marginBottom: 8 }}>
                        No Users Found
                      </Title>
                      <Text type="secondary" style={{ fontSize: 14 }}>
                        {searchQuery 
                          ? `No exact matches found for "${searchQuery}"`
                          : "No users found in the system"}
                      </Text>
                    </div>
                  }
                />
              }
            />
            
            {/* Custom Pagination */}
            {pagination.total > 0 && (
              <CustomPagination />
            )}
          </>
        )}
      </Card>

      {/* Footer Stats */}
      <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
        <Col xs={24} md={8}>
          <Card style={{ borderRadius: '12px', textAlign: 'center' }}>
            <LineChartOutlined style={{ fontSize: 32, color: '#1890ff', marginBottom: 8 }} />
            <Title level={5} style={{ margin: 0 }}>Date Selection</Title>
            <Text type="secondary">
              Selected: {selectedDate.format('DD MMM YYYY')}
            </Text>
            <div style={{ marginTop: 8 }}>
              <Button 
                type="link" 
                size="small"
                onClick={() => setSelectedDate(dayjs())}
              >
                Reset to Today
              </Button>
            </div>
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card style={{ borderRadius: '12px', textAlign: 'center' }}>
            <WalletOutlined style={{ fontSize: 32, color: '#52c41a', marginBottom: 8 }} />
            <Title level={5} style={{ margin: 0 }}>Total Wallet Balance</Title>
            <Text strong style={{ fontSize: 20, color: '#3f8600' }}>
              {formatCurrency(stats.totalWalletBalance)}
            </Text>
            <Text type="secondary">Across all users</Text>
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card style={{ borderRadius: '12px', textAlign: 'center' }}>
            <DollarOutlined style={{ fontSize: 32, color: '#faad14', marginBottom: 8 }} />
            <Title level={5} style={{ margin: 0 }}>Average Wallet</Title>
            <Text strong style={{ fontSize: 20, color: '#fa8c16' }}>
              {formatCurrency(stats.averageWalletBalance)}
            </Text>
            <Text type="secondary">Per user average</Text>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default UsersListPage;